import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import { useParams, Link } from "react-router-dom";
import Slider from "react-slick";
import Breadcrumb from '../Components/Breadcrumb';
import ProductAttributes from '../Components/ProductAttributes';
import SellerInfoCard from '../Components/SellerInfoCard';
import LoadingOverlay from '../Components/LoadingOverlay';
import "../assests/css/sellerDetails.css";

const API = 'https://admin.bridge365.in/wp-json/wc/v3';
const API_BASE = "https://admin.bridge365.in/wp-json";
const CK = "ck_1d0bfb52b9046a39fb2d906f89f691968931bce6";
const CS = "cs_956c51f2c9249f343bec7f0ecbfd7738654ba424";

function getMetaValue(product, keys) {
  return product?.meta_data?.find((item) => keys.includes(item.key))?.value;
}

function getPhoneError(phone) {
  const value = (phone || "").trim();

  if (!value) {
    return "Please enter your mobile number";
  }

  if (/\s/.test(value) || /[^0-9]/.test(value)) {
    return "Mobile number can contain digits only";
  }

  if (value.length !== 10) {
    return "Mobile number must be exactly 10 digits";
  }

  if (!/^[6-9]/.test(value)) {
    return "Mobile number must start with 6, 7, 8 or 9";
  }

  if (/^(\d)\1{9}$/.test(value)) {
    return "Please enter a valid mobile number";
  }

  if (value === "1234567890" || value === "0123456789") {
    return "Please enter a valid mobile number";
  }

  return "";
}

function getProductSellerId(product) {
  return (
    product?.seller?.id ||
    product?.store?.id ||
    product?.vendor?.id ||
    product?.seller_id ||
    product?.vendor_id ||
    product?.author ||
    product?.post_author ||
    getMetaValue(product, ["seller_id", "vendor_id", "_seller_id", "_vendor_id"])
  );
}

function ProductDetail() {

  const { id } = useParams();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [seller, setSeller] = useState(null);
  const [sellerProductCount, setSellerProductCount] = useState(0);

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formStatus, setFormStatus] = useState("form");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    quantity: "1",
    message: ""
  });

  useEffect(() => {
    setActiveImageIndex(0);
    setIsLightboxOpen(false);
  }, [id]);

  // 👉 Fetch product
  useEffect(() => {
    fetch(`${API}/products/${id}?consumer_key=${CK}&consumer_secret=${CS}`)
      .then(res => res.json())
      .then(data => {
        setProduct(data);
        setSeller(data?.seller || data?.store || data?.vendor || null);
        setSellerProductCount(0);
      });
  }, [id]);

  useEffect(() => {
    if (!product) return;

    const sellerId = getProductSellerId(product);

    if (!sellerId) return;

    Promise.all([
      fetch(`${API_BASE}/custom/v1/seller/${sellerId}`).then((res) => res.json()),
      fetch(`${API_BASE}/custom/v1/seller-products/${sellerId}`).then((res) => res.json()),
    ])
      .then(([sellerData, productData]) => {
        setSeller((currentSeller) => ({
          ...(currentSeller || {}),
          ...(sellerData || {}),
          id: sellerId,
        }));
        setSellerProductCount(Array.isArray(productData) ? productData.length : 0);
      })
      .catch(() => {
        setSeller(product.seller || product.store || product.vendor || null);
      });
  }, [product]);

  // 👉 Fetch related products
  useEffect(() => {
    if (!product) return;

    const catId = product.categories[0]?.id;

    if (!catId) return;

    fetch(`${API}/products?category=${catId}&per_page=10&exclude=${product.id}&consumer_key=${CK}&consumer_secret=${CS}`)
      .then(res => res.json())
      .then(data => setRelated(data));

  }, [product]);

  useEffect(() => {
    if (!isLightboxOpen) return;

    const imageCount = product?.images?.length || 0;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsLightboxOpen(false);
      }
      if (event.key === "ArrowRight" && imageCount > 1) {
        setActiveImageIndex((current) => (current + 1) % imageCount);
      }
      if (event.key === "ArrowLeft" && imageCount > 1) {
        setActiveImageIndex((current) => (current - 1 + imageCount) % imageCount);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isLightboxOpen, product]);

  if (!product) return <LoadingOverlay />;

  const relatedSliderSettings = {
    dots: false,
    infinite: related.length > 4,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    arrows: true,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
        }
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 576,
        settings: {
          slidesToShow: 1,
        }
      }
    ]
  };

  const productImages = product.images || [];
  const activeImage = productImages[activeImageIndex] || productImages[0];

  const showPrevImage = () => {
    if (productImages.length < 2) return;
    setActiveImageIndex((current) => (current - 1 + productImages.length) % productImages.length);
  };

  const showNextImage = () => {
    if (productImages.length < 2) return;
    setActiveImageIndex((current) => (current + 1) % productImages.length);
  };

  const resetEnquiryForm = () => {
    setFormData({ name: "", phone: "", quantity: "1", message: "" });
    setFormStatus("form");
    setIsSubmitting(false);
    setPhoneError("");
  };

  const closeEnquiryForm = () => {
    setShowForm(false);
    resetEnquiryForm();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setFormData({ ...formData, phone: digitsOnly });
      setPhoneError(digitsOnly ? getPhoneError(digitsOnly) : "");
      return;
    }

    setFormData({ ...formData, [name]: value });
  };

  const handlePhoneBlur = () => {
    setPhoneError(getPhoneError(formData.phone));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const currentPhoneError = getPhoneError(formData.phone);
    if (currentPhoneError) {
      setPhoneError(currentPhoneError);
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await fetch(
        "https://admin.bridge365.in/wp-json/custom/v1/enquiry",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            name: formData.name,
            phone: formData.phone,
            quantity: formData.quantity,
            message: formData.message,
            product: product.name,
            product_id: product.id
          })
        }
      );

      const data = await res.json();

      if (data.status === "success") {
        setFormStatus("success");
      } else {
        setFormStatus("error");
      }
    } catch (error) {
      setFormStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

// console.log(productid);

  return (
    <div>
      <Helmet>
        <title>{product.name}</title>
      </Helmet>

      <Header />

      <div className='container main-contant'>
        <div className='inner-page-container'>

          <Breadcrumb
            categoryIds={product.categories?.map((category) => category.id)}
            currentLabel={product.name}
          />

          <div className='product-info-box product-detail-layout'>

            {/* 🔥 IMAGE */}
            <div className="product-detail-media">
              <button
                type="button"
                className="pdp-main-image-btn"
                onClick={() => setIsLightboxOpen(true)}
              >
                <img
                  src={product.images[activeImageIndex]?.src || product.images[0]?.src}
                  alt={product.name}
                  className="pdp-main-image"
                />
              </button>

              <div className="pdp-gallery">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`pdp-gallery-thumb-btn${index === activeImageIndex ? " is-active" : ""}`}
                    onClick={() => setActiveImageIndex(index)}
                  >
                    <img
                      src={img.src}
                      alt={`${product.name} ${index + 1}`}
                      className="pdp-gallery-thumb"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* 🔥 DETAILS */}
            <div className="product-detail-content">
              <h1>{product.name}</h1>
              <div
                dangerouslySetInnerHTML={{ __html: product.short_description }}
              />

              <ProductAttributes
                attributes={product.attributes}
                className="product-attributes-detail"
              />

              <button
                className="btn enquiry-btn"
                onClick={() => {
                  resetEnquiryForm();
                  setShowForm(true);
                }}
              >
                Contact Now
              </button>
            </div>

            {seller && (
              <aside className="product-detail-seller">
                <SellerInfoCard
                  seller={seller}
                  productCount={sellerProductCount || seller.product_count}
                />
              </aside>
            )}

          </div>


          <div className='product-detail-description'>
            <div
              dangerouslySetInnerHTML={{ __html: product.description }}
            />
          </div>

          {/* 🔥 RELATED PRODUCTS SLIDER */}
          {related.length > 0 && (
            <div className="pdp-related">
              <h2>You may also like</h2>

              <div className="product-related-slider">
                <Slider {...relatedSliderSettings}>
                  {related.map((item) => (
                    <div key={item.id}>
                      <div className="pdp-related-card">
                        <Link to={`/product/${item.id}`} className="pdp-related-link">
                          <div className="pdp-related-image-wrap">
                            <img
                              src={item.images[0]?.src}
                              alt={item.name}
                              className="pdp-related-image"
                            />
                          </div>

                          <h4 className="pdp-related-title">{item.name}</h4>
                          <p className="pdp-related-sku">{item.sku || item.id}</p>
                        </Link>
                      </div>
                    </div>
                  ))}
                </Slider>
              </div>
            </div>
          )}

        </div>
      </div>

      {showForm && (
        <div className="enquiry-overlay" onClick={closeEnquiryForm}>
          <div className="enquiry-modal" onClick={(e) => e.stopPropagation()}>
            <div className="enquiry-modal-header">
              <div>
                <h3 className="enquiry-modal-title">{product.name}</h3>
                {formStatus === "form" && (
                  <p className="enquiry-modal-subtitle">Share your details and we will contact you soon.</p>
                )}
              </div>
              <button
                className="enquiry-modal-x"
                type="button"
                aria-label="Close enquiry form"
                onClick={closeEnquiryForm}
              >
                ×
              </button>
            </div>

            {formStatus === "success" ? (
              <div className="enquiry-success">
                <div className="enquiry-success-icon" aria-hidden="true">✓</div>
                <h4 className="enquiry-success-title">Enquiry Sent Successfully</h4>
                <p className="enquiry-success-text">
                  Thank you, <strong>{formData.name}</strong>. We have received your enquiry
                  for <strong>{formData.quantity}</strong> {Number(formData.quantity) === 1 ? "unit" : "units"} of{" "}
                  <strong>{product.name}</strong>. Our team will contact you shortly on{" "}
                  <strong>{formData.phone}</strong>.
                </p>
                <button className="enquiry-submit-btn" type="button" onClick={closeEnquiryForm}>
                  Done
                </button>
              </div>
            ) : formStatus === "error" ? (
              <div className="enquiry-success enquiry-error">
                <div className="enquiry-error-icon" aria-hidden="true">!</div>
                <h4 className="enquiry-success-title">Could not send enquiry</h4>
                <p className="enquiry-success-text">
                  Something went wrong while sending your enquiry. Please try again.
                </p>
                <button className="enquiry-submit-btn" type="button" onClick={() => setFormStatus("form")}>
                  Try Again
                </button>
                <button className="enquiry-close-btn" type="button" onClick={closeEnquiryForm}>
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="enquiry-form">
                <input
                  className="enquiry-input"
                  name="name"
                  placeholder="Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />

                <div className="enquiry-field">
                  <input
                    className={`enquiry-input${phoneError ? " enquiry-input-error" : ""}`}
                    name="phone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    maxLength="10"
                    placeholder="Mobile Number"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={handlePhoneBlur}
                    required
                  />
                  {phoneError && <p className="enquiry-field-error">{phoneError}</p>}
                </div>

                <input
                  className="enquiry-input"
                  name="quantity"
                  type="number"
                  min="1"
                  placeholder="Quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  required
                />

                <textarea
                  className="enquiry-textarea"
                  name="message"
                  placeholder="Message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                />

                <button className="enquiry-submit-btn" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Sending..." : "Submit"}
                </button>

                <button
                  className="enquiry-close-btn"
                  type="button"
                  onClick={closeEnquiryForm}
                >
                  Close
                </button>
              </form>
            )}
          </div>
        </div>
      )}

      {isLightboxOpen && activeImage && (
        <div className="pdp-lightbox" onClick={() => setIsLightboxOpen(false)}>
          <button
            type="button"
            className="pdp-lightbox-close"
            aria-label="Close image preview"
            onClick={() => setIsLightboxOpen(false)}
          >
            ×
          </button>

          {productImages.length > 1 && (
            <button
              type="button"
              className="pdp-lightbox-nav pdp-lightbox-prev"
              aria-label="Previous image"
              onClick={(event) => {
                event.stopPropagation();
                showPrevImage();
              }}
            >
              ‹
            </button>
          )}

          <img
            src={activeImage.src}
            alt={product.name}
            className="pdp-lightbox-image"
            onClick={(event) => event.stopPropagation()}
          />

          {productImages.length > 1 && (
            <button
              type="button"
              className="pdp-lightbox-nav pdp-lightbox-next"
              aria-label="Next image"
              onClick={(event) => {
                event.stopPropagation();
                showNextImage();
              }}
            >
              ›
            </button>
          )}

          {productImages.length > 1 && (
            <div className="pdp-lightbox-thumbs" onClick={(event) => event.stopPropagation()}>
              {productImages.map((img, index) => (
                <button
                  key={index}
                  type="button"
                  className={`pdp-lightbox-thumb${index === activeImageIndex ? " is-active" : ""}`}
                  onClick={() => setActiveImageIndex(index)}
                >
                  <img src={img.src} alt={`${product.name} ${index + 1}`} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <Footer />

    </div>
  );
}

export default ProductDetail;
